export const MAGIC_0 = 0x44; // 'D'
export const MAGIC_1 = 0x44; // 'D'
export const PROTOCOL_VERSION = 1;
export const HEADER_SIZE = 16;

export enum FrameType {
  KEYFRAME = 0x01,
  P_FRAME = 0x02,
  CODEC_CONFIG = 0x03,
  HEARTBEAT_PING = 0x04,
  HEARTBEAT_PONG = 0x05,
  INPUT_EVENT = 0x06,
  KEYFRAME_REQUEST = 0x07,
}

export interface BinaryFrame {
  type: FrameType;
  sequenceNumber: number;
  timestampMs: number;
  payload: Uint8Array;
}

export interface TouchInputPayload {
  action: number; // 0=Down, 1=Move, 2=Up, 3=Cancel
  normalizedX: number; // 0.0 - 1.0
  normalizedY: number; // 0.0 - 1.0
  pointerId: number;
}

/**
 * Decode a binary packet received from WebSocket
 */
export function decodeBinaryFrame(data: ArrayBuffer): BinaryFrame | null {
  if (data.byteLength < HEADER_SIZE) {
    return null;
  }

  const view = new DataView(data);
  const m0 = view.getUint8(0);
  const m1 = view.getUint8(1);
  const version = view.getUint8(2);
  const frameType = view.getUint8(3);

  if (m0 !== MAGIC_0 || m1 !== MAGIC_1 || version !== PROTOCOL_VERSION) {
    return null;
  }

  const sequenceNumber = view.getUint32(4, false); // big-endian
  const timestampMs = view.getUint32(8, false);
  const payloadLength = view.getUint32(12, false);

  if (data.byteLength < HEADER_SIZE + payloadLength) {
    return null;
  }

  const payload = new Uint8Array(data, HEADER_SIZE, payloadLength);

  return {
    type: frameType as FrameType,
    sequenceNumber,
    timestampMs,
    payload,
  };
}

/**
 * Encode an input event packet to send upstream to phone
 */
export function encodeInputEvent(input: TouchInputPayload, seq: number): ArrayBuffer {
  // Input payload: 1 byte action, 4 bytes float X, 4 bytes float Y, 4 bytes u32 pointerId = 13 bytes
  const payloadSize = 13;
  const buffer = new ArrayBuffer(HEADER_SIZE + payloadSize);
  const view = new DataView(buffer);

  // Header
  view.setUint8(0, MAGIC_0);
  view.setUint8(1, MAGIC_1);
  view.setUint8(2, PROTOCOL_VERSION);
  view.setUint8(3, FrameType.INPUT_EVENT);
  view.setUint32(4, seq, false);
  view.setUint32(8, Date.now() & 0xffffffff, false);
  view.setUint32(12, payloadSize, false);

  // Payload
  view.setUint8(16, input.action);
  view.setFloat32(17, input.normalizedX, false);
  view.setFloat32(21, input.normalizedY, false);
  view.setUint32(25, input.pointerId, false);

  return buffer;
}

/**
 * Encode a Keyframe request packet
 */
export function encodeKeyframeRequest(seq: number): ArrayBuffer {
  const buffer = new ArrayBuffer(HEADER_SIZE);
  const view = new DataView(buffer);

  view.setUint8(0, MAGIC_0);
  view.setUint8(1, MAGIC_1);
  view.setUint8(2, PROTOCOL_VERSION);
  view.setUint8(3, FrameType.KEYFRAME_REQUEST);
  view.setUint32(4, seq, false);
  view.setUint32(8, Date.now() & 0xffffffff, false);
  view.setUint32(12, 0, false);

  return buffer;
}
